import { useFeatureFlagEnabled } from "@client/hooks/use-feature-flag-enabled";
import { trpc } from "@client/trpc";
import { Button, Card, Skeleton } from "@sfc/ui";
import { Link } from "wouter";
import { TeamListItems } from "../teams/TeamListItems";

export function HomeTeams({
  viewerId,
  showEmpty = true,
}: {
  viewerId: string;
  showEmpty?: boolean;
}) {
  const enabled = useFeatureFlagEnabled("teams");
  if (!enabled) return null;
  return <HomeTeamsList viewerId={viewerId} showEmpty={showEmpty} />;
}

function HomeTeamsList({
  viewerId,
  showEmpty,
}: {
  viewerId: string;
  showEmpty: boolean;
}) {
  const query = trpc.listTeams.useQuery({ all: false, archived: false });
  // React remounts do not clear cached query data when impersonation changes.
  const teams =
    query.data?.viewerId === viewerId ? query.data.teams : undefined;
  // Optional sections appear only once we know they have content (or an error).
  if (!showEmpty && !query.isError && !teams?.length) return null;
  return (
    <section
      aria-labelledby="home-applications-heading"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 id="home-applications-heading" className="heading-lg">
            Applications
          </h2>
          <p className="body-md text-text-secondary">
            Your teams for applying together.
          </p>
        </div>
        <Button
          variant="nav"
          className="pointer-coarse:min-h-11"
          nativeButton={false}
          render={<Link href="/teams" />}
        >
          View all teams
        </Button>
      </div>
      <section aria-label="My Teams">
        <Card className="py-0 overflow-hidden">
          {!query.isError && teams === undefined ? (
            <Skeleton
              className="h-20 w-full"
              aria-label="Loading teams"
              role="status"
            />
          ) : query.isError ? (
            <div role="alert">
              <p>Couldn’t load your teams.</p>
              <Button variant="nav" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </div>
          ) : teams?.length ? (
            <TeamListItems teams={teams} hrefForTeam={(id) => `/teams/${id}`} />
          ) : (
            <p className="body-md text-text-secondary p-5">
              You haven’t joined a team yet. Create one from My Teams, or accept
              an invitation.
            </p>
          )}
        </Card>
      </section>
    </section>
  );
}
